import React, { useState, useMemo } from "react";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ExpandLess,
  ExpandMore,
  HomeOutlined,
  CalendarTodayOutlined,
  DashboardOutlined,
  GroupsOutlined,
  AdminPanelSettingsOutlined,
  AssessmentOutlined,
  AssignmentOutlined,
  ReceiptOutlined,
  RateReviewOutlined,
  NotificationsOutlined,
  PhoneOutlined,
  EventNoteOutlined,
  WorkOutline,
  TrendingUp,
  CalendarMonth,
  BarChart,
  PieChart,
  AccountBalanceWalletOutlined,
  AccountBalance,
  AppsOutlined,
  BusinessCenterOutlined,
  HourglassEmpty,
  SpaOutlined,
  Event,
  PersonSearchOutlined,
  DescriptionOutlined,
  BadgeOutlined,
  FilterAltOutlined,
  TuneOutlined,
  ArticleOutlined,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const NavItem = ({ item, isActive, pathname, navigate, theme }) => (
  <ListItemButton
    onClick={() => navigate(`/${item.link}`)}
    sx={{
      pl: 3,
      backgroundColor: isActive
        ? theme.palette.mode === "dark"
          ? "rgba(249, 115, 22, 0.15)"
          : "rgba(249, 115, 22, 0.08)"
        : "transparent",
      borderLeft: isActive
        ? `3px solid ${theme.palette.secondary.main}`
        : "3px solid transparent",
      "&:hover": {
        backgroundColor: theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : theme.palette.grey[100],
      },
    }}
  >
    <ListItemIcon
      sx={{
        color: isActive ? theme.palette.secondary.main : theme.palette.text.secondary,
        minWidth: 36,
      }}
    >
      {item.icon}
    </ListItemIcon>
    <ListItemText
      primary={item.text}
      sx={{
        "& .MuiListItemText-primary": {
          color: isActive
            ? theme.palette.mode === "dark"
              ? "#ffffff"
              : theme.palette.text.primary
            : theme.palette.text.secondary,
          fontWeight: isActive ? 600 : 400,
          fontSize: "0.875rem",
        },
      }}
    />
  </ListItemButton>
);

const Sidebar = ({ drawerWidth = "250px", isSidebarOpen, setIsSidebarOpen, isNonMobile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const user = useSelector((state) => state.global.user) || {
    name: "Just a moment...",
    role: "member",
  };

  const role = user.role ?? "member";

  // ChatGPT Phase 2: New information architecture
  // Structure: id, title, icon, items[{ text, link, icon, roles }]
  const navStructure = useMemo(() => [
    { id: "dashboard", standalone: true, text: "Dashboard", icon: <HomeOutlined />, link: "dashboard", roles: ["admin", "superadmin", "lead", "member", "caller", "assistant"] },
    {
      id: "workHub",
      title: "Work Hub",
      icon: <WorkOutline />,
      items: [
        { text: "Applications", link: "applications", icon: <WorkOutline />, roles: ["admin", "superadmin", "lead", "caller", "assistant"] },
        { text: "My Interviews", link: "interviews", icon: <EventNoteOutlined />, roles: ["caller", "assistant"] },
        { text: "Interview List", link: "interview-list", icon: <EventNoteOutlined />, roles: ["admin", "superadmin", "caller", "assistant"] },
        { text: "Interview Schedule", link: "interview-schedule", icon: <EventNoteOutlined />, roles: ["lead"] },
        { text: "Tasks", link: "tasks", icon: <AssignmentOutlined />, roles: ["admin", "superadmin", "caller"] },
        { text: "Calendar", link: "calendar", icon: <CalendarTodayOutlined />, roles: ["admin", "superadmin", "lead", "member", "caller", "assistant"] },
        { text: "Kanban", link: "kanban", icon: <DashboardOutlined />, roles: ["member"] },
        { text: "My Daily Progress", link: "my-progress", icon: <BarChart />, roles: ["member"] },
      ],
    },
    {
      id: "teamManagement",
      title: "Team Management",
      icon: <GroupsOutlined />,
      items: [
        { text: "Daily Logs", link: "members", icon: <GroupsOutlined />, roles: ["admin", "superadmin", "lead"] },
        { text: "Callers", link: "callers", icon: <PhoneOutlined />, roles: ["admin", "superadmin"] },
        // Temporarily hidden — see Notifications → Temporarily hidden features
        // { text: "Profile Mapping", link: "profile-mapping", icon: <PersonAddOutlined />, roles: ["admin", "lead"] },
      ],
    },
    { id: "performance", standalone: true, text: "Performance", icon: <AssessmentOutlined />, link: "performance", roles: ["admin", "superadmin", "lead", "assistant", "caller"] },
    {
      id: "businessManagement",
      title: "Business Management",
      icon: <BusinessCenterOutlined />,
      items: [
        { text: "Overview", link: "business/overview", icon: <TrendingUp />, roles: ["admin", "superadmin"] },
        { text: "Active Roles", link: "business/active", icon: <WorkOutline />, roles: ["admin", "superadmin"] },
        { text: "Pending Roles", link: "business/pending", icon: <HourglassEmpty />, roles: ["admin", "superadmin"] },
        { text: "Payment Schedule", link: "business/payments", icon: <Event />, roles: ["admin", "superadmin"] },
        { text: "Bank Management", link: "business/banks", icon: <AccountBalance />, roles: ["admin", "superadmin"] },
      ],
    },
    {
      id: "hiring",
      title: "Hiring",
      icon: <PersonSearchOutlined />,
      items: [
        { text: "Roles", link: "hiring/roles", icon: <WorkOutline />, roles: ["admin", "superadmin"] },
        { text: "History", link: "hiring/history", icon: <EventNoteOutlined />, roles: ["admin", "superadmin"] },
        { text: "Compare", link: "hiring/compare", icon: <AssessmentOutlined />, roles: ["admin", "superadmin"] },
        { text: "API config", link: "hiring/api-config", icon: <AdminPanelSettingsOutlined />, roles: ["admin", "superadmin"] },
      ],
    },
    {
      id: "resumeOps",
      title: "Resume Ops",
      icon: <DescriptionOutlined />,
      items: [
        { text: "Overview", link: "resume-ops/overview", icon: <TrendingUp />, roles: ["admin", "superadmin"] },
        { text: "Documents", link: "resume-ops/documents", icon: <DescriptionOutlined />, roles: ["admin", "superadmin"] },
        { text: "Workers", link: "resume-ops/workers", icon: <BadgeOutlined />, roles: ["admin", "superadmin"] },
        { text: "Applications", link: "resume-ops/applications", icon: <ArticleOutlined />, roles: ["admin", "superadmin"] },
        { text: "Job Filtering", link: "resume-ops/job-filtering", icon: <FilterAltOutlined />, roles: ["admin", "superadmin"] },
        { text: "Settings", link: "resume-ops/settings", icon: <TuneOutlined />, roles: ["admin", "superadmin"] },
      ],
    },
    {
      id: "finance",
      title: "Finance",
      icon: <ReceiptOutlined />,
      items: [
        { text: "Overview", link: "finance/overview", icon: <TrendingUp />, roles: ["admin", "superadmin", "lead", "caller", "member"] },
        { text: "Invoices", link: "finance/invoices", icon: <ReceiptOutlined />, roles: ["admin", "superadmin"] },
        { text: "Cash Flow", link: "finance/cash-flow", icon: <AccountBalanceWalletOutlined />, roles: ["admin", "superadmin"] },
        { text: "Accounts", link: "finance/accounts", icon: <AppsOutlined />, roles: ["admin", "superadmin"] },
        { text: "Invoices & Payroll", link: "transactions", icon: <ReceiptOutlined />, roles: ["lead"] },
        { text: "My Invoices", link: "finance/my-invoice", icon: <ReceiptOutlined />, roles: ["caller"] },
        { text: "Transactions", link: "finance/transactions", icon: <ReceiptOutlined />, roles: ["member"] },
      ],
    },
    {
      id: "notificationsFeedback",
      title: "Notifications & Feedback",
      icon: <NotificationsOutlined />,
      items: [
        { text: "Notification Center", link: "notifications", icon: <NotificationsOutlined />, roles: ["admin", "superadmin", "lead", "caller", "member", "assistant"] },
        { text: "Feedback", link: "feedback", icon: <RateReviewOutlined />, roles: ["admin", "superadmin", "lead", "caller", "member", "assistant"] },
      ],
    },
    {
      id: "adminPanel",
      title: "Admin Panel",
      icon: <AdminPanelSettingsOutlined />,
      items: [
        { text: "Admin Console", link: "admin", icon: <AdminPanelSettingsOutlined />, roles: ["admin", "superadmin"] },
      ],
    },
    // Break / wellness — visible to every logged-in role
    { id: "recharge", standalone: true, pinBottom: true, text: "Recharge", icon: <SpaOutlined />, link: "recharge" },
  ], [role, user]);

  // Filter to sections/items visible for current role.
  // Standalone entries with no `roles` array are available to everyone.
  const visibleNav = useMemo(() => {
    return navStructure.filter((section) => {
      if (section.standalone) {
        if (!section.roles?.length) return true;
        return section.roles.includes(role);
      }
      const visibleItems = section.items?.filter((item) => item.roles?.includes(role)) ?? [];
      return visibleItems.length > 0;
    });
  }, [navStructure, role]);

  const mainNav = useMemo(() => visibleNav.filter((s) => !s.pinBottom), [visibleNav]);
  const bottomNav = useMemo(() => visibleNav.filter((s) => s.pinBottom), [visibleNav]);

  const STORAGE_KEY = "sidebar-expanded";

  // Default: expand section containing current path; merge with persisted state
  const getDefaultExpanded = () => {
    const fromPath = {};
    visibleNav.forEach((s) => {
      if (s.items) {
        const hasActive = s.items.some((item) => {
          if (!item.roles?.includes(role)) return false;
          const link = item.link;
          return pathname === `/${link}` || pathname.startsWith(`/${link}/`);
        });
        fromPath[s.id] = hasActive;
      }
    });
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      return { ...stored, ...fromPath }; // Path-based overrides (active route always expanded)
    } catch {
      return fromPath;
    }
  };

  const [expanded, setExpanded] = useState(getDefaultExpanded);

  // Keep section expanded when navigating to a child route; persist
  React.useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      visibleNav.forEach((s) => {
        if (s.items) {
          const hasActive = s.items.some((item) => {
            if (!item.roles?.includes(role)) return false;
            return pathname === `/${item.link}` || pathname.startsWith(`/${item.link}/`);
          });
          if (hasActive) next[s.id] = true;
        }
      });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [pathname, role, visibleNav]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const renderNavItem = (item) => {
    if (!item.roles?.includes(role)) return null;
    const link = item.link;
    const displayText = typeof item.text === "function" ? item.text(role) : item.text;
    const displayItem = { ...item, text: displayText };
    const isActive = pathname === `/${link}` || pathname.startsWith(`/${link}/`);
    return <NavItem key={link} item={displayItem} isActive={isActive} pathname={pathname} navigate={navigate} theme={theme} />;
  };

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant={isNonMobile ? "persistent" : "temporary"}
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.alt,
              boxSizing: "border-box",
              borderRight: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            },
          }}
        >
          <Box p="1.5rem" flexShrink={0}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Endurova Team"
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: "contain",
                    borderRadius: "8px",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="h5" fontWeight="700" sx={{ color: theme.palette.mode === "dark" ? "#ffffff" : theme.palette.text.primary }}>
                  Endurova Team
                </Typography>
              </Box>
              <IconButton onClick={() => setIsSidebarOpen(false)}>
                <ChevronLeft />
              </IconButton>
            </Box>
          </Box>

          <Divider />

          <List sx={{ pt: 0, flex: 1, minHeight: 0, overflowY: "auto" }}>
            {mainNav.map((section) => {
              if (section.standalone) {
                const isActive = pathname === `/${section.link}` || pathname.startsWith(`/${section.link}/`);
                return (
                  <ListItemButton
                    key={section.id}
                    onClick={() => navigate(`/${section.link}`)}
                    sx={{
                      backgroundColor: isActive ? (theme.palette.mode === "dark" ? "rgba(249, 115, 22, 0.15)" : "rgba(249, 115, 22, 0.08)") : "transparent",
                      borderLeft: isActive ? `3px solid ${theme.palette.secondary.main}` : "3px solid transparent",
                      "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : theme.palette.grey[100] },
                    }}
                  >
                    <ListItemIcon sx={{ color: isActive ? theme.palette.secondary.main : theme.palette.text.secondary, minWidth: 40 }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.text}
                      sx={{
                        "& .MuiListItemText-primary": {
                          color: isActive ? (theme.palette.mode === "dark" ? "#ffffff" : theme.palette.text.primary) : theme.palette.text.secondary,
                          fontWeight: isActive ? 600 : 400,
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                  </ListItemButton>
                );
              }

              const isOpen = expanded[section.id] ?? false;
              const hasActiveChild = section.items?.some((item) => {
                if (!item.roles?.includes(role)) return false;
                return pathname === `/${item.link}` || pathname.startsWith(`/${item.link}/`);
              });

              return (
                <React.Fragment key={section.id}>
                  <ListItemButton
                    onClick={() => toggleExpand(section.id)}
                    sx={{
                      "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : theme.palette.grey[100] },
                    }}
                  >
                    <ListItemIcon sx={{ color: hasActiveChild ? theme.palette.secondary.main : theme.palette.text.secondary, minWidth: 40 }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.title}
                      sx={{
                        "& .MuiListItemText-primary": {
                          color: hasActiveChild ? (theme.palette.mode === "dark" ? "#ffffff" : theme.palette.text.primary) : theme.palette.text.secondary,
                          fontWeight: hasActiveChild ? 600 : 400,
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.items?.map((item) => renderNavItem(item))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>

          {/* Wellness / break — pinned under work nav */}
          {bottomNav.length > 0 && (
            <Box flexShrink={0}>
              <Divider />
              <List sx={{ pt: 0, pb: 1 }}>
                {bottomNav.map((section) => {
                  const isActive = pathname === `/${section.link}` || pathname.startsWith(`/${section.link}/`);
                  return (
                    <ListItemButton
                      key={section.id}
                      onClick={() => navigate(`/${section.link}`)}
                      sx={{
                        backgroundColor: isActive ? (theme.palette.mode === "dark" ? "rgba(249, 115, 22, 0.15)" : "rgba(249, 115, 22, 0.08)") : "transparent",
                        borderLeft: isActive ? `3px solid ${theme.palette.secondary.main}` : "3px solid transparent",
                        "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : theme.palette.grey[100] },
                      }}
                    >
                      <ListItemIcon sx={{ color: isActive ? theme.palette.secondary.main : theme.palette.text.secondary, minWidth: 40 }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.text}
                        sx={{
                          "& .MuiListItemText-primary": {
                            color: isActive ? (theme.palette.mode === "dark" ? "#ffffff" : theme.palette.text.primary) : theme.palette.text.secondary,
                            fontWeight: isActive ? 600 : 400,
                            fontSize: "0.9rem",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          )}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
